import { PublicCallback } from 'globals/types';
import { handleError } from 'globals/errors';
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import { getStore, addSessionToStore } from 'core_main/store';
import { createHandoff } from 'core_main/api/handoff';
import { ON_CONVERSATION_CREATE_EVENT_TYPE } from 'core/constants';
import { runListenersForEvent } from 'core_main/globals/events';
import {
  validateHandoffParameters,
  reIdentifyAfterHandoff,
  createHandoffCallbackResponse,
} from './helpers';
import { HandoffConversationParameters, HandoffConversationCallbackResponse } from './types';

/**
 * Creates a handoff conversation with historical messages
 * Matches iOS: Kustomer.handoff()
 *
 * @param parameters - Handoff conversation parameters
 * @param cb - Optional callback function
 * @returns Promise that resolves with the handoff conversation response
 */
export const handoff = (
  parameters: HandoffConversationParameters,
  cb?: PublicCallback,
): Promise<HandoffConversationCallbackResponse> => {
  // Validate SDK is initialized
  const store = getStore();
  if (!store || !store.settings) {
    const error = new Error(
      'SDK not initialized. Call KustomerCore.init() first.',
    );
    if (typeof cb === 'function') {
      cb(null, error);
    }
    return Promise.reject(error);
  }

  // Validate parameters
  const validationError = validateHandoffParameters(parameters);
  if (validationError) {
    if (typeof cb === 'function') {
      cb(null, validationError);
    }
    return Promise.reject(validationError);
  }

  // Ensure tracking token exists (customer must be identified)
  // This is handled by maybeFetchCurrentTrackingToken in createHandoff
  return createHandoff(parameters)
    .then((response) => {
      // Add session to store (similar to createSession)
      addSessionToStore(response.data);

      // Re-identify customer after successful handoff (matches iOS pattern)
      // This ensures customer identity is refreshed, which may update the tracking token
      // with a customer ID if one becomes available
      return reIdentifyAfterHandoff(response);
    })
    .then((response) => {
      const customerId = response.data.relationships?.customer?.data?.id;
      if (!customerId || typeof customerId !== 'string') {
        throw new Error(
          'Invalid handoff response: missing customer ID in response data',
        );
      }
      const { orgId } = getStore();

      // Initialize PubNub if not already connected (similar to createConversation)
      return importAndInitializePubNub({
        customerId,
        orgId,
      }).then((pubnub) => ({ pubnub, response }));
    })
    .then(({ pubnub, response }) => {
      // Check PubNub connection status (matches iOS restartPubNubAfterHandoff pattern)
      // If PubNub initialization failed, log warning but continue - handoff succeeded
      const { isPubNubConnected } = getStore();
      if (!isPubNubConnected) {
        handleError(
          new Error('PubNub not initialized after handoff'),
          {
            message: 'Failed to connect to PubNub after handoff (handoff succeeded):',
          }
        );
        // Continue to return success - handoff API call succeeded (matches iOS behavior)
      } else {
        // Extract conversation channels from response (same structure as createSession)
        const { channels } = response?.data?.attributes?.pubnub?.data || {};
        const sharedConversationChannel = channels?.find(
          (channel) => channel.type === 'shared-conversation'
        );

        const sharedConversationChannelName = sharedConversationChannel?.name;

        const conversationChannels = sharedConversationChannelName
          ? [sharedConversationChannelName]
          : undefined;

        // Subscribe to PubNub channels if available
        // NOTE: This deviates from createConversation's pattern, which waits for subscription
        // confirmation via a status listener before invoking the callback. For handoff:
        // - The backend already has all historical messages, so the handoff itself is complete
        // - Real-time subscription is optional/less critical since messages are already imported
        // - We invoke the callback immediately after handoff succeeds, without waiting for
        //   subscription confirmation, because the primary operation (importing messages) is done
        if (conversationChannels) {
          pubnub.getMetaPubNub()?.instance?.subscribe({
            channels: conversationChannels,
          });
        }
      }

      // Create callback response
      const callbackResponse = createHandoffCallbackResponse(response);

      // Run event listeners
      runListenersForEvent(
        ON_CONVERSATION_CREATE_EVENT_TYPE,
        callbackResponse,
        undefined
      );

      // Invoke callback if provided
      // NOTE: Unlike createConversation, we invoke the callback immediately without waiting
      // for PubNub subscription confirmation. This is intentional because:
      // - The backend already has all historical messages, so the handoff operation is complete
      // - Real-time messaging is secondary for handoff (messages are already imported)
      // - The handoff API call itself succeeded, which is what matters most
      // Always return success even if PubNub initialization failed (matches iOS behavior)
      if (typeof cb === 'function') {
        cb(callbackResponse, undefined);
      }

      return callbackResponse;
    })
    .catch((error) => {
      // For API errors or other failures, propagate normally
      if (typeof cb === 'function') {
        cb(null, error);
      }
      runListenersForEvent(ON_CONVERSATION_CREATE_EVENT_TYPE, null, error);
      handleError(error, { message: 'Failed to create handoff conversation:' });
      throw error;
    });
};
